import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { apiPaths, baseApi } from '@services';
import {
    AddSharedUserArgs,
    AttachClipsArgs,
    CreateEoArgs,
    DeleteEoArgs,
    LimecraftEoResponse,
    LimecraftSharedUserResponse,
    NotifyUserArgs,
    ShareEoArgs,
    ShareEoSequenceArgs,
} from '../types';

const extendedApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        createSelectionShare: builder.mutation<
            LimecraftEoResponse,
            CreateEoArgs
        >({
            query: ({ productionId, title }) => ({
                url: apiPaths.share.createEo(productionId),
                method: 'POST',
                body: {
                    objectType: 'EditorialObject',
                    funnel: 'SelectionShare',
                    title,
                },
            }),
        }),

        attachClips: builder.mutation<LimecraftEoResponse, AttachClipsArgs>({
            query: ({ productionId, eoId, clipIds }) => ({
                url: apiPaths.share.attach(productionId, eoId.toString()),
                method: 'POST',
                body: clipIds,
            }),
        }),

        shareEditorialObject: builder.mutation<string[], ShareEoArgs>({
            query: ({ productionId, eoId, description, title }) => ({
                url: apiPaths.share.shareEo(productionId, eoId.toString()),
                method: 'POST',
                body: { label: title, description },
            }),
        }),

        deleteSelectionShare: builder.mutation<void, DeleteEoArgs>({
            query: ({ productionId, eoId }) => ({
                url: apiPaths.share.deleteEo(productionId, eoId.toString()),
                method: 'DELETE',
            }),
        }),

        addSharedUser: builder.mutation<
            LimecraftSharedUserResponse,
            AddSharedUserArgs
        >({
            query: ({ productionId, shareId, email }) => ({
                url: apiPaths.share.addSharedUser(
                    productionId,
                    shareId.toString(),
                ),
                method: 'POST',
                body: { email },
            }),
        }),

        notifySharedUser: builder.mutation<void, NotifyUserArgs>({
            query: ({ productionId, shareId, sharedUserId, callbackURL }) => ({
                url: apiPaths.share.notifySharedUser(
                    productionId,
                    shareId.toString(),
                    sharedUserId.toString(),
                ),
                method: 'POST',
                body: { callbackURL },
            }),
        }),

        shareClipSequence: builder.mutation<string, ShareEoSequenceArgs>({
            async queryFn(arg, api) {
                let eoId: number | null = null;

                try {
                    // Step 1: Create
                    const { id } = await api
                        .dispatch(
                            extendedApi.endpoints.createSelectionShare.initiate(
                                {
                                    productionId: arg.productionId,
                                    title: arg.title,
                                },
                            ),
                        )
                        .unwrap();
                    eoId = id;

                    // Step 2: Attach
                    await api
                        .dispatch(
                            extendedApi.endpoints.attachClips.initiate({
                                productionId: arg.productionId,
                                eoId,
                                clipIds: arg.clipIds,
                            }),
                        )
                        .unwrap();

                    // Step 3: Generate Token & Extract ID
                    const [token] = await api
                        .dispatch(
                            extendedApi.endpoints.shareEditorialObject.initiate(
                                {
                                    productionId: arg.productionId,
                                    eoId,
                                    title: arg.title,
                                    description: arg.description,
                                },
                            ),
                        )
                        .unwrap();

                    const decodedToken = JSON.parse(atob(token));
                    const shareId = decodedToken.a;
                    const finalUrl = apiPaths.share.generatePublicLink(
                        arg.productionId,
                        token,
                    );

                    // Step 4 & 5: Notify Users (Skips if no emails are provided)
                    if (arg.emails && arg.emails.length > 0) {
                        const notifyPromises = arg.emails.map(async (email) => {
                            const user = await api
                                .dispatch(
                                    extendedApi.endpoints.addSharedUser.initiate(
                                        {
                                            productionId: arg.productionId,
                                            shareId,
                                            email,
                                        },
                                    ),
                                )
                                .unwrap();

                            return api
                                .dispatch(
                                    extendedApi.endpoints.notifySharedUser.initiate(
                                        {
                                            productionId: arg.productionId,
                                            shareId,
                                            sharedUserId: user.id,
                                            callbackURL: finalUrl,
                                        },
                                    ),
                                )
                                .unwrap();
                        });
                        await Promise.all(notifyPromises);
                    }

                    return { data: finalUrl };
                } catch (error) {
                    if (eoId) {
                        api.dispatch(
                            extendedApi.endpoints.deleteSelectionShare.initiate(
                                { productionId: arg.productionId, eoId },
                            ),
                        );
                    }
                    return { error: error as FetchBaseQueryError };
                }
            },
        }),
    }),
});

export const { useShareClipSequenceMutation } = extendedApi;
export default extendedApi.endpoints;
